import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tabs } from '@delmonicos/shared-design-system-react/src/components/tab/tabs.component';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import {
  extractTabKeys,
  useTabQuery,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ChargePointChannelListControllerWithContext } from '../charge-point-channel-list/charge-point-channel-list.controller';
import { TransactionChannelListControllerWithContext } from '../charge-point-channel-transaction-list/charge-point-channel-transaction-list.controller';
import { ChargePointDiagnosticListController } from '../charge-point-diagnostic-list/charge-point-diagnostic-list.controller';
import { useChargePointContext } from '../charge-point.context';
import * as css from './charge-point-details.module.scss';
import { InformationTabComponent } from './tabs/information-tab/information-tab.component';
import { MaintenanceTabComponent } from './tabs/maintenance-tab/maintenance-tab.component';

enum TabsEnum {
  INFORMATION = 'information-tab',
  CHANNELS = 'channels-tab',
  TRANSACTIONS = 'transactions-tab',
  MAINTENANCE = 'maintenance-tab',
}

export const ChargePointDetailsView = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const chargePoint = useChargePointContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [isDiagnosticListDialogOpened, setIsDiagnosticListDialogOpened] =
    useState(false);

  // TABS //////////////////////////////////////////////////////////////////////

  const tabs = useMemo(
    () =>
      [
        {
          title: t('$.charge-point-details.information.title'),
          icon: 'electric-terminal-3',
          key: TabsEnum.INFORMATION,
        },
        {
          title: t('delmonicos-shared-sdk.outputs-charge-point.channels'),
          icon: 'electric-terminal',
          key: TabsEnum.CHANNELS,
        },
        {
          title: t('$.charge-point-details.transaction-list.title'),
          icon: 'currency-euro-circle',
          key: TabsEnum.TRANSACTIONS,
        },
        {
          title: t('$.charge-point-details.maintenance.title'),
          icon: 'tool-02',
          key: TabsEnum.MAINTENANCE,
        },
      ] as const,
    [t],
  );

  const { tab, setTab } = useTabQuery(
    TabsEnum.INFORMATION,
    extractTabKeys(tabs),
  );

  // TODO: Each tab should have a set of rules that allow it to manage how
  // search parameters are handled
  // Refactor design of Tab component to do it
  const handleTabClick = (tabNext: (typeof tabs)[number]['key']) => {
    if (searchParams.has('channelId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('channelId');
      next.set('tab', tabNext);
      setSearchParams(next, { replace: true });
    } else {
      setTab(tabNext);
    }
  };

  const channel = chargePoint?.channels.at(0);

  const renderTab = () => {
    switch (tab) {
      case TabsEnum.INFORMATION:
        if (!channel) {
          // TODO: Improve rendering of this unexpected case
          return (
            <Text>This charge point is not associated with any channel</Text>
          );
        }

        return <InformationTabComponent />;
      case TabsEnum.CHANNELS:
        if (!channel) {
          // TODO: Improve rendering of this unexpected case
          return (
            <Text>This charge point is not associated with any channel</Text>
          );
        }

        return <ChargePointChannelListControllerWithContext />;
      case TabsEnum.TRANSACTIONS:
        return <TransactionChannelListControllerWithContext />;
      case TabsEnum.MAINTENANCE:
        return (
          <MaintenanceTabComponent
            openDiagnosticListDialog={() =>
              setIsDiagnosticListDialogOpened(true)
            }
          />
        );
      default:
        // TODO: Refactor this display
        return <div>No tab selected</div>;
    }
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack
      className={css.chargePointDetails}
      direction="column"
      gap={GapEnum.XL}>
      <Tabs currentTab={tab} elements={tabs} onTabClick={handleTabClick} />
      {renderTab()}
      <Dialog
        open={isDiagnosticListDialogOpened}
        onClose={() => setIsDiagnosticListDialogOpened(false)}
        size={DialogSize.MEDIUM}>
        <Dialog.Header
          icon="file-search-02"
          onClose={() => setIsDiagnosticListDialogOpened(false)}
          title={t('$.charge-point-diagnostic-list.title')}
        />
        <ChargePointDiagnosticListController />
      </Dialog>
    </Stack>
  );
};
